import type { ButtonHTMLAttributes } from 'react';

import type { TPaymentMethodId } from '@/modules/perseo-billing/components/accordion/interfaces';
import type {
  IItemGTM,
  SavedCardReturnData,
  TBillingOrderConfirmationProductItem,
  TOrderConfirmationDelivery
} from '@/modules/perseo-billing/interfaces/src/bff-web/billing';
import type { IProcessorResponse } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/orderV2';
import type {
  IGTMEvent,
  IGtmPageVirtualEvent,
  IGTMSendProductsBilling,
  IGTMSendProductsBillingPurchase
} from '@/modules/perseo-billing/interfaces/src/front';
import type { GtmEvent } from '@/modules/shared/utils/gtmEvents/types';

export type IPaymentOptions =
  | 'EPAY_CENCOSUD_TCOM'
  | 'EPAY_TCOM'
  | 'WEBPAY'
  | 'GIFT_CARD'
  | 'CENCOPAY'
  | 'CLUB';

export interface IPaymentDetails {
  id: IPaymentOptions;
}

export interface IPaymentMethod {
  paymentDetails: IPaymentDetails;
  paymentInfo?: { [key: string]: string | number | boolean };
}

export type TSavedCardProps = SavedCardReturnData & {
  paymentMethodId?: string;
};

export type TAccordionItem = {
  id: 'CLUB_PARIS';
  title: string;
  isInvoiceCompatible: boolean;
};

export type TCardInputs = 'cardNumber' | 'name' | 'expiry' | 'cvv' | undefined;

export interface ICreditCardForm {
  cardNumber: string;
  name: string;
  expiry: string;
  cvv: string;
  installments: number;
  saveCard?: boolean;
  termsAndConditions: boolean;
  focusedInput?: TCardInputs;
  numberLastDigits?: string;
  cardType?: string;
}

export interface ICreditCardPreview {
  focusedInput?: TCardInputs;
  numberLastDigits?: string;
  cardType?: string;
  card?: TSavedCardProps;
  isSavedCard?: boolean;
  isExpired: boolean;
}

interface IPriceDetails {
  price: string;
  isCencoPrice: boolean;
  isColabPrice: boolean;
  isSampling: boolean;
}

export enum EServiceType {
  'warranty',
  'install',
  'recycle',
  'assemble'
}

export interface IItem {
  sku?: string;
  img?: string;
  title?: string;
  quantity?: number;
  specification?: string;
  priceDetails?: IPriceDetails;
  isService?: boolean;
  isSampling?: boolean;
  isSecondHand?: boolean;
  isPresale?: boolean;
  serviceType?: EServiceType;
  isEmailTrigger?: boolean;
  gtm?: IItemGTM;
  prices?: IPrice[];
}

export type TDeliveryType = 'STORE_PICKUP' | 'INTANGIBLE' | 'RECEIVES' | 'HOME';

export interface IBasketSummary {
  soldBy?: string;
  sendBy?: string;
  deliveryDetails?: TOrderConfirmationDelivery;
  items?: TBillingOrderConfirmationProductItem[];
  timeStamp?: string;
}

export type TInvoiceValues = {
  id: string;
  officialName: string;
  areaOfWork: string;
  address: string;
  addressNumber: number;
  numberOfHouseApartmentOrOffice?: string;
  regionId: string;
  communeId: string;
};

export type TCustomerInfo = {
  customerName?: string;
  customerRut?: string;
  customerEmail?: string;
  customerPhone?: string;
};

export interface IPrice {
  id: string;
  price: number;
  priceFormat: string;
  discountPercentage?: string;
  isCencoPrice?: boolean;
  isCencoPayPrice?: boolean;
  isColabPrice?: boolean;
  isStrikedThrough?: boolean;
}

interface CnstrcPurchaseData {
  items: CnstrcPurchaseDataItem[];
  order_id: string;
  revenue: number;
}

export interface CnstrcPurchaseDataItem {
  item_id: string;
  variation_id: string;
  count: number;
  price: number;
}

declare global {
  interface Window {
    perseoData?: PerseoDataType;
    newrelic?: NewRelicType;
    webkit?: WebkitType;
    AnalyticsWebInterface?: AnalyticsWebInterfaceType;
    opera?: string;
    MSStream?: string;
    Spreedly: SpreedlyType;
    cnstrcUserId?: string;
    cnstrcPurchaseData?: CnstrcPurchaseData;
  }

  // Define the types for each property
  export interface PerseoDataType {
    isSlasSession?: boolean;
    customerId?: string;
    sessionId?: string;
    dwsid?: string;
    userData?: {
      customer?: {
        // Define the structure of the customer object
        [key: string]: string;
      };
    };
    hidden?: boolean;
    switches?: {
      perseoGlobal?: {
        isBilling3DsEnabled?: boolean;
      };
    };
    tokens?: {
      [key: string]: string;
    };
  }

  interface WebkitMessage {
    command: string;
    name: string;
    parameters:
      | IGTMSendProductsBilling
      | IGTMEvent
      | IGtmPageVirtualEvent
      | IGTMSendProductsBillingPurchase
      | GtmEvent
      | Record<string, unknown>;
  }

  interface WebkitType {
    messageHandlers: {
      [key: string]: {
        postMessage: (message: WebkitMessage) => void;
      };
    };
  }

  interface AnalyticsWebInterfaceType {
    logEvent: (event: string, data: string) => void;
  }
  interface NewRelicType {
    log: (error: Error | string) => void;
    noticeError: (error: Error | string) => void;
    addPageAction: (action: string, value?: Record<string, string>) => void;
    setCustomAttribute: (name: string, value: string | number) => void;
    interaction: () => {
      setAttribute: (name: string, value: string) => void;
    };
  }

  interface SpreedlyEventMap {
    ready: [];

    fieldEvent: [
      name: spreedly.SpreedlyField,
      event: string,
      activeElement: string,
      inputData:
        | {
            numberLength?: number;
            [key: string]: unknown;
          }
        | undefined
    ];

    paymentMethod: [
      inputProperties: string,
      pmData: spreedly.SpreedlyPaymentMethod
    ];

    validation: [
      inputProperties: {
        cardType: string;
        isValid: boolean;
        fields: { [fieldName: string]: boolean };
      },
      pmData: spreedly.SpreedlyPaymentMethod
    ];

    errors: [errors: spreedly.SpreedlyError[]];
  }

  interface SpreedlyType {
    init: (
      id: string,
      options: {
        numberEl: string;
        cvvEl: string;
        nonce: string;
        timestamp: string;
        certificateToken: string;
        signature: string;
      }
    ) => void;

    on<K extends keyof SpreedlyEventMap>(
      event: K,
      callback: (...args: SpreedlyEventMap[K]) => void
    ): void;
    setPlaceholder: (field: string, placeholder: string) => void;
    setFieldType: (field: string, type: string) => void;
    setNumberFormat: (format: string) => void;
    setStyle: (field: string, style: string) => void;
    validate: () => void;
    reload: () => void;
    removeHandlers: () => void;
    tokenizeCreditCard: (data: {
      email: string;
      dni: string;
      rut: string;
      first_name: string;
      last_name: string;
      full_name: string;
      month: string;
      year: string;
    }) => void;
    toggleMask: () => void;
  }
}
export interface IAvailablePaymentMethodsResponse {
  wallets: IPaymentMethodResponse[];
  savedCards: ISavedCardResponse[];
  paymentMethods: IPaymentMethodResponse[];
  sessionKey: ISessionKey;
  cencosudCardBINs: string[];
  clubParis: IClubParis;
  cencoFees: CencoFee[];
  creditFees: CreditFee[];
  spreedlyInstance: ISpreedlyInstance;
}

export interface CencoFee {
  fee: number;
}

export interface CreditFee {
  fee: number;
}

export interface IClubParis {
  id: string;
  title: string;
  subTitle: string;
  isInvoiceCompatible: boolean;
  enabled: boolean;
  disableText: string;
  isCencopayCompatible: boolean;
  isGiftCardCompatible: boolean;
  canPayWithNovios: boolean;
  componentType: string;
  tooltip: {
    title: string;
    desccription: string;
  };
}

export interface IPaymentMethodResponse {
  viewSort: number;
  methodId: string;
  enabled: boolean;
  name: string;
  title: string;
  subTitle: string;
  processorId: string;
  canPayWithInvoice: boolean;
  canPayWithGiftCard: boolean;
  canPayWithCencopay: boolean;
  canPayWithMkpEasyProducts: boolean;
  canPayWithNovios: boolean;
  attributes: IAttributeResponse[];
  customerGroup: string;
}

interface ISessionKey {
  idKey: string;
  publicKey: string;
}

export interface IAttributeResponse {
  label: string;
  value: string;
}

export interface ISavedCardResponse {
  brand: string;
  lastDigits: string;
  token: string;
  bin: string;
  isFavorite: boolean;
  isExpired: boolean;
  methodId: TPaymentMethodId;
}

export interface ISpreedlyInstance {
  certificateToken?: string;
  nonce: string;
  timestamp: string;
  signature: string;
}

export interface IClubParis {
  id: string;
  title: string;
  subTitle: string;
  isInvoiceCompatible: boolean;
  enabled: boolean;
  disableText: string;
  isCencopayCompatible: boolean;
  isGiftCardCompatible: boolean;
  componentType: string;
  tooltip: {
    title: string;
    desccription: string;
  };
}

export interface IPaymentMethodResponse {
  viewSort: number;
  methodId: string;
  enabled: boolean;
  name: string;
  title: string;
  subTitle: string;
  processorId: string;
  canPayWithInvoice: boolean;
  canPayWithGiftCard: boolean;
  canPayWithCencopay: boolean;
  canPayWithMkpEasyProducts: boolean;
  canPayWithNovios: boolean;
  attributes: IAttributeResponse[];
  customerGroup: string;
}

export interface IAttributeResponse {
  label: string;
  value: string;
}

export interface Payment {
  id: string;
  amountPlanned: AmountPlanned;
  paymentMethodInfo: PaymentMethodInfo;
  paymentStatus: PaymentStatus;
  transactions: Transaction[];
  interfaceInteractions: InterfaceInteraction[];
}
export interface AmountPlanned {
  type: string;
  currencyCode: string;
  centAmount: number;
  fractionDigits: number;
}
export interface PaymentMethodInfo {
  paymentInterface: string;
  method: string;
  name: Name;
}
export interface Name {
  'es-CL': string;
}
export interface PaymentStatus {
  interfaceCode: string;
  interfaceText: string;
}
export interface Transaction {
  id: string;
  timestamp: string;
  type: string;
  amount: Amount;
  interactionId: string;
  state: string;
}
export interface Amount {
  type: string;
  currencyCode: string;
  centAmount: number;
  fractionDigits: number;
}

export interface InterfaceInteraction {
  interactionId: string;
  tentativeOrderNumber?: string;
  token: string;
  giftCardDetails: GiftCardDetails;
  processorResponse?: IProcessorResponse;
}
export interface GiftCardDetails {
  token: string;
  isNovios: boolean;
  giftCardType: string;
  details: Detail[];
  balance: number;
}
export interface Detail {
  amount: number;
  depto: string;
}

export type TPaymentMethod = IPaymentOptions | 'all';

export interface IPaymentButton
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string;
  method: TPaymentMethod;
  loading?: boolean;
  customHandler?: () => void;
}
